import axios, { AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import {
  AdminCandidateIssues,
  AdminCandidateSearchResponse,
  AdminHiringDateRange,
  AdminHiringJobMetricsResponse,
  AdminHiringMetricsSummary,
  AdminInterviewTranscription,
  EmailSchedulerOption,
} from "@type/admin";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const searchCandidatesAdmin = async (
  q: string,
  limit = 20
): Promise<AxiosResponse<{ data: AdminCandidateSearchResponse }>> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/admin/candidates/search`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    params: { q, limit },
  });
};

export const getCandidateIssues = async (
  user_id: string
): Promise<AxiosResponse<{ data: AdminCandidateIssues }>> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/admin/candidates/${user_id}/issues`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
};

export const getInterviewTranscription = async (
  conversation_id: string
): Promise<AxiosResponse<{ data: AdminInterviewTranscription }>> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/admin/interviews/${conversation_id}/transcription`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
};

export const getAdminHiringMetrics = async (params: {
  company_id?: string;
  range: AdminHiringDateRange;
}): Promise<AxiosResponse<{ data: AdminHiringMetricsSummary }>> => {
  const authToken = getCookie("authToken");
  const { company_id, range } = params;
  return await axios({
    url: `${apiURL}/admin/hiring-activity/metrics`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    params: {
      start_date: range.start_date,
      end_date: range.end_date,
      ...(company_id ? { company_id } : {}),
    },
  });
};

export const getAdminHiringJobMetrics = async (params: {
  company_id: string;
  range: AdminHiringDateRange;
}): Promise<AxiosResponse<{ data: AdminHiringJobMetricsResponse }>> => {
  const authToken = getCookie("authToken");
  const { company_id, range } = params;
  return await axios({
    url: `${apiURL}/admin/hiring-activity/jobs`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    params: {
      company_id,
      start_date: range.start_date,
      end_date: range.end_date,
    },
  });
};

type RawAdminCompany = {
  id?: string;
  company_id?: string;
  name?: string;
  company_name?: string;
};

type RawAdminCompanyJob = {
  id?: string;
  job_id?: string;
  job_title?: string;
  title?: string;
};

/**
 * Admin: the full list of companies across all workspaces — for cross-company
 * pickers such as the Maya Chat Simulator (#3105).
 *
 * GET /companies/admin/list  →  { data: [{ id | company_id, name | company_name }] }
 *
 * Field naming is normalized here (id/company_id, name/company_name) so the FE
 * is robust to the exact BE key names; confirm the shape against the BE.
 */
export const getAdminCompanyList = async (): Promise<
  { id: string; name: string }[]
> => {
  const authToken = getCookie("authToken");
  const res = await axios({
    url: `${apiURL}/companies/admin/list`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
  const list = (res?.data?.data ?? []) as RawAdminCompany[];
  return list
    .map((c) => ({
      id: c.id ?? c.company_id ?? "",
      name: c.name ?? c.company_name ?? "",
    }))
    .filter((c) => c.id && c.name);
};

/**
 * Admin: the jobs belonging to a given company (#3105).
 *
 * GET /companies/admin/{company_id}/jobs  →  { data: [{ id, job_title | title }] }
 */
export const getAdminCompanyJobs = async (
  companyId: string
): Promise<{ id: string; job_title: string }[]> => {
  const authToken = getCookie("authToken");
  const res = await axios({
    url: `${apiURL}/companies/admin/${companyId}/jobs`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
  const rows = (res?.data?.data ?? []) as RawAdminCompanyJob[];
  return rows
    .map((j) => ({
      id: j.id ?? j.job_id ?? "",
      job_title: j.job_title ?? j.title ?? "",
    }))
    .filter((j) => j.id);
};

export const getEmailSchedulers = async (): Promise<
  AxiosResponse<{ data: EmailSchedulerOption[] }>
> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/admin/email-schedulers`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
};

export const triggerEmailSchedulerSimulation = async (params: {
  company_id: string;
  scheduler_key: string;
  as_of_date?: string;
}): Promise<
  AxiosResponse<{
    code: number;
    message: string;
    data: { queued_at: string } | null;
    errors?: string[];
  }>
> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/admin/email-schedulers/simulate`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    data: params,
  });
};

// ---------------------------------------------------------------------------
// Admin "Users" menu (#3365, BE contract ourteam-NEW/ourteam-backend#4256 —
// proposal, confirm before relying on exact field names).
//   GET /users/admin/list?page&limit&search
// `search` is a single query the BE ORs across username / first_name /
// last_name / email (partial, case-insensitive). Admin-only; 403 otherwise.

export interface AdminUserItem {
  id: string;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
  company_name?: string | null;
  created_at?: string | null;
  last_login_at?: string | null;
  // #3372 (BE ourteam-backend#4263): job-seeker rows only — the latest
  // application's context for the flow columns + opening the candidate
  // drawer. null on HR rows / absent on an older BE.
  candidate_context?: {
    candidate_id?: string | null;
    latest_application?: {
      application_id?: string | null;
      job_id?: string | null;
      job_title?: string | null;
      company_name?: string | null;
      applicant_status?: string | null;
      flow_progress?: { completed: number; total: number; percent: number } | null;
      current_step?: { id: string; type: string; label: string; index: number | null; total: number } | null;
    } | null;
  } | null;
}

export interface AdminUsersListResult {
  users: AdminUserItem[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
  // #3372: tab badges; absent until the BE ships them.
  role_group_counts?: { job_seeker?: number; hr?: number } | null;
}

export const getAdminUsersList = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  // #3372: "job_seeker" | "hr" — absent keeps today's unfiltered behavior.
  role_group?: "job_seeker" | "hr";
}): Promise<AdminUsersListResult> => {
  const authToken = getCookie("authToken");
  const res = await axios({
    url: `${apiURL}/users/admin/list`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      ...(params.search?.trim() ? { search: params.search.trim() } : {}),
      ...(params.role_group ? { role_group: params.role_group } : {}),
    },
  });
  const users = (res?.data?.data ?? []) as AdminUserItem[];
  const pagination = res?.data?.meta?.pagination ?? {
    total: users.length,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    total_pages: 1,
  };
  return { users, pagination, role_group_counts: res?.data?.meta?.role_group_counts ?? null };
};

/**
 * Admin: delete a user account (#3368, BE contract ourteam-NEW/ourteam-backend#4259
 * — proposal; self-delete returns 400, non-admin 403, unknown id 404; the BE
 * audits every deletion). Callers surface error messages verbatim.
 *   DELETE /users/admin/{user_id}
 */
export const deleteAdminUser = async (userId: string): Promise<void> => {
  const authToken = getCookie("authToken");
  await axios({
    url: `${apiURL}/users/admin/${userId}`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
};
